import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSecureStorage from '../../utils/adminSecureStorage';
import { PATHS } from '../../routes/routes';
import { getCustomers, addPoints, Customer, AddPointsRequest } from '../../api/admin';

// interface PointTransaction {
//   id: string;
//   customerId: string;
//   points: number;
//   description: string;
//   type: 'MANUAL' | 'PURCHASE' | 'REDEMPTION' | 'REFUND';
//   createdAt: string;
// }

const AdminPoints = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [points, setPoints] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  useEffect(() => {
    // Check authentication
    if (!AdminSecureStorage.isAdminTokenValid()) {
      navigate(PATHS.ADMIN_LOGIN);
      return;
    }

    loadCustomers();
  }, [navigate]);

  useEffect(() => {
    // Filter customers based on search
    if (searchEmail.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.email.toLowerCase().includes(searchEmail.toLowerCase()) ||
        (customer.fullName && customer.fullName.toLowerCase().includes(searchEmail.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    }
  }, [customers, searchEmail]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomers(1, 100, searchEmail);
      setCustomers(response.data.customers);
      setFilteredCustomers(response.data.customers);
    } catch (error: any) {
      setError('Failed to load customers: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customerId: string) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    }
  };

  const handleAddPoints = async () => {
    if (selectedCustomers.length === 0) {
      setError('Please select at least one customer');
      return;
    }

    if (!points || isNaN(Number(points)) || Number(points) <= 0) {
      setError('Please enter a valid positive number of points');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const requestData: AddPointsRequest = {
        customerIds: selectedCustomers,
        points: Number(points),
        description: description.trim()
      };

      const response = await addPoints(requestData);
      
      if (response.success) {
        setSuccess(response.message);
        setPoints('');
        setDescription('');
        setSelectedCustomers([]);
        
        // Reload customers to get updated points
        await loadCustomers();
      } else {
        setError('Failed to add points');
      }
    } catch (error: any) {
      setError('Failed to add points: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    AdminSecureStorage.removeAdminToken();
    navigate(PATHS.ADMIN_LOGIN);
  };

  if (loading && customers.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Manage Customer Points</h1>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(PATHS.ADMIN_DASHBOARD)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Back to Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Points Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Points to Customers</h2>
          
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points to Add
              </label>
              <input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter number of points"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Bonus points, Compensation, etc."
              />
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={handleAddPoints}
              disabled={loading || selectedCustomers.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Adding Points...' : `Add Points to ${selectedCustomers.length} Customer(s)`}
            </button>
          </div>
        </div>

        {/* Customer List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Select Customers</h2>
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search by email or name..."
                />
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {selectedCustomers.length === filteredCustomers.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {selectedCustomers.length} of {filteredCustomers.length} customers selected
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedCustomers.includes(customer.id) ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleCustomerSelect(customer.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.includes(customer.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCustomerSelect(customer.id);
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        {customer.fullName || 'No Name'}
                      </p>
                      <p className="text-sm text-gray-500">{customer.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {customer.currentPoints.toLocaleString()} points
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredCustomers.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-gray-500">No customers found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPoints;
